import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Loader2, Sparkles, Trophy, Info, ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { HustleBadge, type HustleLevel } from "@/components/HustleBadge";
import { formatMoney } from "@/lib/money";

interface HistoryRow {
  id: string;
  old_score: number;
  new_score: number;
  delta: number;
  old_level: HustleLevel | string;
  new_level: HustleLevel | string;
  reason: string;
  counterpart_id: string | null;
  metadata: { tx_id?: string; amount_cents?: number } | null;
  created_at: string;
}

const REASON_LABEL: Record<string, string> = {
  transfer_sent: "Sent payment",
  transfer_received: "Received payment",
  tier_change: "Tier change",
  referral_qualified: "Referral joined",
  recalc: "Score recalculated",
  backfill: "Initial score",
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Mirror of public.recalc_hustle_score formula for per-row breakdown.
const breakdownFor = (row: HistoryRow): string | null => {
  const cents = row.metadata?.amount_cents ?? 0;
  if (row.reason === "transfer_sent") {
    const volPts = Math.floor(cents / 1000);
    return `+1 (transaction) +${volPts} (volume: ${formatMoney(cents)} ÷ $10)`;
  }
  if (row.reason === "transfer_received") {
    return "+1 (received transaction)";
  }
  if (row.reason === "referral_qualified") {
    return "+5 (qualified referral)";
  }
  if (row.reason === "tier_change") {
    return "Level updated based on total score";
  }
  return null;
};

const PointsActivity = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [counterparts, setCounterparts] = useState<Record<string, string>>({});
  const [score, setScore] = useState<{ score: number; level: HustleLevel | string } | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [{ data: prof }, { data }] = await Promise.all([
        supabase
          .from("profiles")
          .select("hustle_score, hustle_level")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("hustle_score_history")
          .select("id, old_score, new_score, delta, old_level, new_level, reason, counterpart_id, metadata, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(200),
      ]);
      if (cancelled) return;
      if (prof) setScore({ score: prof.hustle_score ?? 0, level: prof.hustle_level ?? "Bronze" });
      const list = (data as HistoryRow[] | null) ?? [];
      setRows(list);
      const ids = Array.from(new Set(list.map((r) => r.counterpart_id).filter(Boolean))) as string[];
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, cashtag")
          .in("user_id", ids);
        if (cancelled) return;
        const map: Record<string, string> = {};
        (profs ?? []).forEach((p: { user_id: string; cashtag: string }) => {
          map[p.user_id] = p.cashtag;
        });
        setCounterparts(map);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <main className="min-h-screen bg-background text-foreground pb-16">
      <Helmet>
        <title>Points Activity — Cash Isle</title>
        <meta name="description" content="See how every send and receive earns Hustle points." />
      </Helmet>

      <header className="border-b border-border">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="p-1 -ml-1 rounded-lg hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="font-display font-bold text-lg">Points Activity</h1>
        </div>
      </header>

      <div className="max-w-md mx-auto px-5 pt-5 space-y-5">
        {/* Current score */}
        <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Hustle Score</p>
            <p className="font-display text-3xl font-extrabold mt-1 tabular-nums">
              {score?.score ?? "—"}
            </p>
          </div>
          {score && <HustleBadge level={score.level} />}
        </div>

        {/* Formula card */}
        <div className="rounded-2xl border border-border bg-muted/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-primary" />
            <h2 className="font-display font-bold text-sm">How points are earned</h2>
          </div>
          <ul className="text-xs space-y-2 text-foreground/90">
            <li className="flex justify-between gap-3">
              <span>Each completed transaction (send or receive)</span>
              <span className="font-semibold text-primary tabular-nums">+1</span>
            </li>
            <li className="flex justify-between gap-3">
              <span>Per $10 sent (volume)</span>
              <span className="font-semibold text-primary tabular-nums">+1</span>
            </li>
            <li className="flex justify-between gap-3">
              <span>Each qualified referral</span>
              <span className="font-semibold text-primary tabular-nums">+5</span>
            </li>
          </ul>
          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            Formula:{" "}
            <code className="font-mono text-foreground">
              tx_count + ⌊volume_cents ÷ 1000⌋ + (qualified_referrals × 5)
            </code>
            . Your level (Bronze → Silver → Gold → Platinum) updates automatically as your score grows.
          </p>
        </div>

        {/* Activity log */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-accent" />
            <h2 className="font-display font-bold text-sm">Activity</h2>
          </div>

          {rows === null ? (
            <div className="flex justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              No points activity yet. Send or receive a payment to start earning.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((r) => {
                const breakdown = breakdownFor(r);
                return (
                  <li key={r.id} className="py-3 flex items-start gap-3">
                    <RowIcon reason={r.reason} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight flex items-center gap-2 flex-wrap">
                        {REASON_LABEL[r.reason] ?? r.reason}
                        {r.counterpart_id && counterparts[r.counterpart_id] && (
                          <span className="text-muted-foreground font-normal">
                            · ${counterparts[r.counterpart_id]}
                          </span>
                        )}
                        {r.metadata?.amount_cents !== undefined && r.reason !== "tier_change" && (
                          <span className="text-muted-foreground font-normal">
                            · {formatMoney(r.metadata.amount_cents)}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {formatRelative(r.created_at)} · score {r.old_score} → {r.new_score}
                      </p>
                      {breakdown && (
                        <p className="text-[11px] text-foreground/70 mt-1 font-mono">
                          {breakdown}
                        </p>
                      )}
                      {r.reason === "tier_change" && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <HustleBadge level={r.old_level} size="sm" />
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          <HustleBadge level={r.new_level} size="sm" />
                        </div>
                      )}
                    </div>
                    {r.delta !== 0 && (
                      <span
                        className={
                          "text-sm font-semibold tabular-nums shrink-0 " +
                          (r.delta > 0 ? "text-primary" : "text-destructive")
                        }
                      >
                        {r.delta > 0 ? "+" : ""}
                        {r.delta}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-center text-[11px] text-muted-foreground">
          <Link to="/wallet" className="hover:underline">
            Back to wallet
          </Link>
        </p>
      </div>
    </main>
  );
};

const RowIcon = ({ reason }: { reason: string }) => {
  const base = "h-9 w-9 rounded-full grid place-items-center shrink-0";
  if (reason === "tier_change") {
    return (
      <div className={base + " bg-accent/20 text-accent-foreground"}>
        <Trophy className="h-4 w-4" />
      </div>
    );
  }
  if (reason === "transfer_sent") {
    return (
      <div className={base + " bg-primary/15 text-primary"}>
        <ArrowUpRight className="h-4 w-4" />
      </div>
    );
  }
  if (reason === "transfer_received") {
    return (
      <div className={base + " bg-secondary text-secondary-foreground"}>
        <ArrowDownLeft className="h-4 w-4" />
      </div>
    );
  }
  return (
    <div className={base + " bg-muted text-muted-foreground"}>
      <Sparkles className="h-4 w-4" />
    </div>
  );
};

export default PointsActivity;
